"use client";

import { Link } from "react-router-dom";
import { ImagePlus } from "lucide-react";
import type { ProductRow } from "@/lib/types/product";
import { apiBaseURL } from "@/lib/api-config";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type Props = {
  data: ProductRow[];
  selected: Record<string, boolean>;
  onToggle: (id: number, checked: boolean) => void;
  onEdit: (p: ProductRow) => void;
  onImage: (p: ProductRow) => void;
  onDelete: (p: ProductRow) => void;
};

function imgSrc(url: string | null | undefined) {
  if (!url) {
    return "";
  }
  if (url.startsWith("http")) {
    return url;
  }

  return `${apiBaseURL}${url}`;
}

export function ProductGrid({ data, selected, onToggle, onEdit, onImage, onDelete }: Props) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {data.map((p) => {
        const src = imgSrc(p.image_url);
        const checked = selected[String(p.id)] ?? false;

        return (
          <Card
            key={p.id}
            className={cn(
              "retail-card-hover overflow-hidden",
              checked && "ring-2 ring-accent",
            )}
          >
            <button
              type="button"
              title={src ? "View image" : "Add image"}
              onClick={() => onImage(p)}
              className="group relative block aspect-[4/3] w-full bg-muted"
            >
              {src ? (
                <img
                  src={src}
                  alt=""
                  className="h-full w-full object-cover transition group-hover:scale-[1.02]"
                />
              ) : (
                <span className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-muted-foreground">
                  <ImagePlus className="h-8 w-8" />
                  <span className="text-xs">Add image</span>
                </span>
              )}
              <input
                type="checkbox"
                className="absolute left-2 top-2 h-4 w-4"
                checked={checked}
                onChange={(e) => onToggle(p.id, e.target.checked)}
                onClick={(e) => e.stopPropagation()}
                aria-label={`Select ${p.name}`}
              />
            </button>
            <div className="space-y-1 p-3">
              <p className="line-clamp-2 font-medium leading-snug">{p.name}</p>
              <p className="font-mono text-xs text-muted-foreground">{p.sku}</p>
              <div className="flex items-center justify-between pt-1">
                <span className="font-mono text-sm font-semibold">
                  ₹{Number(p.selling_price).toFixed(2)}
                </span>
                {p.is_active ? (
                  <Badge variant="secondary">Active</Badge>
                ) : (
                  <Badge variant="outline">Off</Badge>
                )}
              </div>
              <div className="flex gap-2 pt-2">
                <Link
                  to={`/products/${p.id}`}
                  className={cn(buttonVariants({ variant: "outline", size: "sm" }), "flex-1 text-center")}
                >
                  View
                </Link>
                <Button
                  variant="secondary"
                  size="sm"
                  className="flex-1"
                  onClick={() => onEdit(p)}
                >
                  Edit
                </Button>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2 w-full border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={() => onDelete(p)}
              >
                Delete
              </Button>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
